import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import { TabContent, Collapse } from 'reactstrap'
// import { ExternalLink } from 'lucide-react'
import Loader from '../../../../../Material/Loader'
import './ExternalIntegrations.css'
import InteractionContainer from '../../../../Interactions/InteractionsContainer'
import DriverContextPanel from './DriverContextPanel'
import { ALL_CONVERSATIONS } from '../../../../../Communication/Queries'
import CommsConversation from '../../../../../Communication/CommsConversation'
import CommsNewConversationModal from '../../../../../Communication/CommsNewConversationModal'
import { HasPermissionsQuery } from '../../../../../Functions/querys'
import DriverTasksTab from '../TasksTab/DriverTasksTab'

const DriverOpenTaskCountQuery = gql`query DriverOpenTaskCount($driverId: ID!){
  driver(id: $driverId){
    id
    driverOpenTaskCount
  }
}`

const TABS = [
  { id: 'tasks', name: 'TASKS', logoComponent: <i className="fa fa-columns"></i> },
  { id: 'interactions', name: 'INTERACTIONS', logoComponent: <i className="fa fa-comments"></i> },
  { id: 'driverContext', name: 'DRIVER CONTEXT', logoComponent: <i className="fa fa-id-card-o"></i> },
  { id: 'conversations', name: 'CONVERSATIONS', requiredPermission: 'view_conversation', logoComponent: <i className="fa fa-envelope"></i> },
]

const ConversationsTab = ({ driverId }) => {
  const [openConversation, setOpenConversation] = useState('')
  const [isNewConversationModalOpen, setIsNewConversationModalOpen] = useState(false)
  const { data, loading, refetch } = useQuery(ALL_CONVERSATIONS, { variables: { driver: driverId, last: 10 }, skip: !driverId, fetchPolicy: 'cache-and-network'})

  const handleConversationCreated = (newConversationId) => {
    setIsNewConversationModalOpen(false)
    setOpenConversation(newConversationId)
    refetch()
  }

  if (loading) return <Loader />

  const conversations = data?.allConversations?.edges || []

  return (
    <div className="conversations-tab">
      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="driver-task-create-button"
          onClick={() => setIsNewConversationModalOpen(true)}
        >
          <i className="fa fa-plus-square" aria-hidden="true" />
          New Conversation
        </button>
      </div>
      {conversations.length === 0 ? (
        <p>No conversations found for this driver.</p>
      ) : (
        <ul className="collapse-ul">
          {conversations.map(({ node: conversation }) => {
            const subject = conversation.subject || conversation.contactName || 'Conversation';
            const inboxName = conversation?.channel?.inbox?.name || 'Inbox';
            return (
              <li key={conversation.id} className="collapse-li">
                <button
                  className={`collapse-button`}
                  type="button"
                  onClick={() => setOpenConversation(openConversation === conversation.id ? '' : conversation.id)}
                >
                  <span className="collapse-button-label">
                    {subject} - {inboxName}
                  </span>
                  <span className="collapse-button-meta">
                    <i
                      className={`collapse-arrow fa fa-chevron-down`}
                      aria-hidden="true"
                    />
                  </span>
                </button>
                {/* Render only when open: inside a closed Collapse (display:none) every element height reads 0, so CommsConversation can't scroll to the newest message. */}
                <Collapse isOpen={openConversation === conversation.id}>
                  {openConversation === conversation.id && (
                    <CommsConversation conversationIdFromParent={conversation.id} slimView={true} />
                  )}
                </Collapse>
              </li>
            )
          })}
        </ul>
      )}
      {isNewConversationModalOpen && (
        <CommsNewConversationModal
          isOpen={isNewConversationModalOpen}
          toggle={() => setIsNewConversationModalOpen(false)}
          defaultDriverId={driverId}
          onCreated={handleConversationCreated}
        />
      )}
    </div>
    )
}



const ExternalIntegrations = ({ driverId }) => {
  const [tab, setTab] = useState('tasks')
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const { data: driverData } = useQuery(DriverOpenTaskCountQuery, {
    variables: { driverId },
    skip: !driverId,
    fetchPolicy: 'network-only',
  })
  const {  data: permissionsData } = useQuery(HasPermissionsQuery, {
        variables: {
            userPermissions: ["view_conversation", "view_taskrabbit"],
    },
  });
  const openTaskCount = driverData?.driver?.driverOpenTaskCount || 0
  const userPermissions = permissionsData?.hasPermissions || [];
  const togglePanel = (tabName) => {
    if (tab === tabName && isPanelOpen) {
      setIsPanelOpen(false)
    } else {
      setTab(tabName)
      setIsPanelOpen(true)
    }
  }

  const currentTabName = useMemo(() => TABS.find(item => item.id === tab)?.name || '', [tab])

  if (!driverId) return null

  return (
    <div className="external-integrations">
      {/* Floating side icons */}
      <div className="integration-icon-container">
        {TABS.map(({ id, name, logoComponent, requiredPermission }) => {
          if (requiredPermission && !userPermissions.includes(requiredPermission)) {
            return null;
          }
          const hasOpenTasks = id === 'tasks' && openTaskCount > 0
          return (
            <div
              key={id}
              className={`integration-icon ${tab === id && isPanelOpen ? 'active' : ''} ${hasOpenTasks ? 'has-open-tasks' : ''}`}
              onClick={() => togglePanel(id)}
              title={name}
            >
              {logoComponent}
              {hasOpenTasks && (
                <span className="integration-open-task-count" aria-label={`${openTaskCount} open tasks`}>
                  {openTaskCount}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Slide panel */}
      <div className={`integration-panel ${isPanelOpen ? 'open' : ''}`}>
        <div className="integration-panel-header">
          <h5>{currentTabName}</h5>
          <button className="close-btn" onClick={() => setIsPanelOpen(false)}>
            ✕
          </button>
        </div>
        <div className="integration-panel-body">
          <TabContent activeTab={tab}>
            {tab === 'interactions' && <InteractionContainer driverId={driverId} interactionsType="driver" />}
            {tab === 'driverContext' && <DriverContextPanel driverId={driverId} />}
            {tab === 'tasks' && <DriverTasksTab driverId={driverId} isSidebar />}
            {tab === 'conversations' && <ConversationsTab driverId={driverId} />}
          </TabContent>
        </div>
      </div>

      {/* Overlay for background click */}
      {/* {isPanelOpen && <div className="panel-overlay" onClick={() => setIsPanelOpen(false)} />} */}
    </div>
  )
}

export default ExternalIntegrations
